import { apiRequest, PortalRefusal } from "@/data/api/client";
import { readSaleClose } from "@/data/mappers/partnerActivity.mapper";
import {
  payloadToOfferCancellation,
  payloadToPartnerOffer,
  type OfferCancellationPayload,
  type PartnerOfferPayload,
} from "@/data/mappers/partnerOffer.mapper";
import { parseStoredCustomFields } from "@/domain/custom-fields/schema";
import { fromMinorUnits } from "@/data/mappers/money.mapper";
import type {
  CustomFieldDefinition,
  StoredCustomFields,
} from "@/domain/custom-fields/types";
import type { SaleEndAnchor } from "@/domain/activity/types";
import type { SlotHours } from "@/domain/offer/rules";
import type { OfferCancellation, PartnerOffer, SaleWindow } from "@/domain/offer/types";

export interface OfferActivityOption {
  id: number;
  name: string;
  priceOriginal: number | null;
  /** What the activity asks at checkout — what an offer on it inherits. Null =
   *  it asks nothing; `unreadableCustomFields` = it asks something stored that
   *  this portal cannot render, which still governs an offer inheriting it. */
  customFields: StoredCustomFields;
  /** What publishing a termín on this template prefills, and what its sale
   *  window is derived from (ADR 0007). Null = the partner names it. The price
   *  is koruny; the API states it in haléře. */
  defaultPriceDiscounted: number | null;
  defaultCapacity: number | null;
  saleLeadTimeHours: number | null;
  saleEndOffsetMinutes: number | null;
  saleEndAnchor: SaleEndAnchor | null;
  defaultDurationMinutes: number | null;
}

interface OfferActivityOptionPayload {
  id: number;
  name: string;
  priceOriginal: number | null;
  customFields?: unknown;
  defaultPriceDiscountedMinorUnits?: number | null;
  defaultCapacity?: number | null;
  saleLeadTimeHours?: number | null;
  saleEndOffsetMinutes?: number | null;
  saleEndAnchor?: string | null;
  defaultDurationMinutes?: number | null;
}

export interface SlotDraft {
  activityId: number;
  /** Omitted by a business with one site: it has no picker, and the API
   *  answers an omitted site with the business's own. */
  siteId?: number;
  startTime: Date;
  endTime: Date;
  capacity: number;
}

export interface OfferPublication {
  slotIds: number[];
  priceDiscounted: number;
  saleWindow: SaleWindow;
  notes: string | null;
  /** Null hands the questions back to the activity; the API stores a real SQL
   *  null for it, which is what makes the fallback genuine. */
  customFields: CustomFieldDefinition[] | null;
}

export interface OfferPatch {
  priceDiscounted?: number;
  saleWindow?: SaleWindow;
  notes?: string | null;
  /** Omitted leaves the stored questions alone; null clears them back to the
   *  activity's. The two are not the same request. */
  customFields?: CustomFieldDefinition[] | null;
}

/** Both bounds always travel together, so emptying a field clears the stored
 *  bound rather than leaving the old one standing. */
function saleWindowBody(window: SaleWindow) {
  return {
    activeTimeStart: window.startsAt?.toISOString() ?? null,
    activeTimeEnd: window.endsAt?.toISOString() ?? null,
  };
}

/** A publish carries the ids back to the API and a failed one deletes them
 *  again, so an answer that is not a list of numbers has to stop here — sent
 *  onwards it would publish a sleva on nothing, or delete `undefined`. */
function createdSlotIds(slots: unknown): number[] {
  const ids = Array.isArray(slots) ? slots.map((slot: { id?: unknown }) => slot?.id) : [];
  const areAllNumbers = ids.every((id) => typeof id === "number");
  if (ids.length === 0 || !areAllNumbers) {
    throw new PortalRefusal("Termín se nepodařilo založit — zkuste to prosím znovu.");
  }
  return ids as number[];
}

export const partnerOffersRepo = {
  async list(): Promise<PartnerOffer[]> {
    const payloads = await apiRequest<PartnerOfferPayload[]>("/v1/partner/offers");
    return payloads.map(payloadToPartnerOffer);
  },

  async listActivityOptions(): Promise<OfferActivityOption[]> {
    const payloads = await apiRequest<OfferActivityOptionPayload[]>("/v1/partner/activities");
    return payloads.map((payload) => ({
      id: payload.id,
      name: payload.name,
      priceOriginal: payload.priceOriginal,
      customFields: parseStoredCustomFields(payload.customFields),
      defaultPriceDiscounted: fromMinorUnits(payload.defaultPriceDiscountedMinorUnits ?? null),
      defaultCapacity: payload.defaultCapacity ?? null,
      saleLeadTimeHours: payload.saleLeadTimeHours ?? null,
      ...readSaleClose(payload),
      defaultDurationMinutes: payload.defaultDurationMinutes ?? null,
    }));
  },

  /** The hour itself. It exists before anything is sold on it, which is why a
   *  publish that fails afterwards has to take it away again. */
  async createSlot(draft: SlotDraft): Promise<number[]> {
    const slots = await apiRequest<{ id: number }[]>("/v1/partner/slots", {
      method: "POST",
      body: {
        activityId: draft.activityId,
        ...(draft.siteId === undefined ? {} : { siteId: draft.siteId }),
        startTime: draft.startTime.toISOString(),
        endTime: draft.endTime.toISOString(),
        capacity: draft.capacity,
      },
    });
    return createdSlotIds(slots);
  },

  async publish({
    slotIds,
    priceDiscounted,
    saleWindow,
    notes,
    customFields,
  }: OfferPublication): Promise<void> {
    await apiRequest("/v1/partner/offers", {
      method: "POST",
      body: { slotIds, priceDiscounted, ...saleWindowBody(saleWindow), notes, customFields },
    });
  },

  async updateOffer(offerId: number, patch: OfferPatch): Promise<void> {
    await apiRequest(`/v1/partner/offers/${offerId}`, {
      method: "PATCH",
      body: {
        ...(patch.priceDiscounted === undefined
          ? {}
          : { priceDiscounted: patch.priceDiscounted }),
        ...(patch.saleWindow === undefined ? {} : saleWindowBody(patch.saleWindow)),
        ...(patch.notes === undefined ? {} : { notes: patch.notes }),
        ...(patch.customFields === undefined ? {} : { customFields: patch.customFields }),
      },
    });
  },

  /** The API refuses a move the moment anything is sold on the hour, which is
   *  the rule the editor only explains. */
  async moveSlot(slotId: number, hours: SlotHours): Promise<void> {
    await apiRequest(`/v1/partner/slots/${slotId}`, {
      method: "PATCH",
      body: {
        startTime: hours.startTime.toISOString(),
        endTime: hours.endTime.toISOString(),
      },
    });
  },

  async setCapacity(slotId: number, capacity: number): Promise<void> {
    await apiRequest(`/v1/partner/slots/${slotId}`, { method: "PATCH", body: { capacity } });
  },

  async removeSlot(slotId: number): Promise<void> {
    await apiRequest(`/v1/partner/slots/${slotId}`, { method: "DELETE" });
  },

  async previewCancellation(offerId: number): Promise<OfferCancellation> {
    const payload = await apiRequest<OfferCancellationPayload>(
      `/v1/partner/offers/${offerId}/cancel-preview`,
    );
    return payloadToOfferCancellation(payload);
  },

  /** Cancels and refunds everyone booked; the sleva itself survives. */
  async cancelReservations(offerId: number): Promise<OfferCancellation> {
    const payload = await apiRequest<OfferCancellationPayload>(
      `/v1/partner/offers/${offerId}/cancel`,
      { method: "POST" },
    );
    return payloadToOfferCancellation(payload);
  },

  async stopSelling(offerId: number, at: Date): Promise<void> {
    await apiRequest(`/v1/partner/offers/${offerId}`, {
      method: "PATCH",
      body: { activeTimeEnd: at.toISOString() },
    });
  },

  async remove(offerId: number): Promise<void> {
    await apiRequest(`/v1/partner/offers/${offerId}`, { method: "DELETE" });
  },
};
